import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { Subscription, timer } from 'rxjs';
import {
  MigrationProcessing,
  ScheduledTaskQueueItem
} from 'src/app/models/migration-processing';
import { DataService } from 'src/app/services';

type MonitorView = 'status' | 'applications' | 'advanced';
type TaskStatus = 'Queued' | 'Processing' | 'Completed' | 'Cancelled' | 'Error';

interface ApplicationTaskRow {
  id?: string;
  queueItem: ScheduledTaskQueueItem;
  applicationIdentifier: string;
  status: TaskStatus;
  queuedOn?: Date;
  executedOn?: Date;
  completedOn?: Date;
  queuedDuration: string;
  executionDuration: string;
  errorMessage?: string;
  workerLabel: string;
}

@Component({
  selector: 'app-settings-migrate-workflow-monitor',
  standalone: false,
  templateUrl: './settings-migrate-workflow-monitor.component.html',
  styleUrls: ['./settings-migrate-workflow-monitor.component.scss']
})
export class SettingsMigrateWorkflowMonitorComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() processing: MigrationProcessing | null = null;
  @Output() workflowTaskCompleted = new EventEmitter<void>();

  activeView: MonitorView = 'status';
  taskQueueItems: ScheduledTaskQueueItem[] = [];
  loading = false;
  cancellingTaskId = '';
  errorMessage = '';

  private pollingSub?: Subscription;
  private notifiedWorkflowTaskId = '';

  constructor(private dataService: DataService) {}

  ngOnInit(): void {
    this.startPolling();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['processing']?.currentValue?.id !== changes['processing']?.previousValue?.id) {
      this.notifiedWorkflowTaskId = '';
    }
    this.loadQueueItems();
  }

  ngOnDestroy(): void {
    this.pollingSub?.unsubscribe();
  }

  get workflowTasks(): ScheduledTaskQueueItem[] {
    return this.taskQueueItems.filter(
      item => item.taskType === 'migrate-workflow'
    );
  }

  get coordinatorTasks(): ScheduledTaskQueueItem[] {
    return this.taskQueueItems.filter(
      item => item.taskType === 'migrate-workflow-applications-coordinator'
    );
  }

  get applicationTasks(): ScheduledTaskQueueItem[] {
    return this.taskQueueItems.filter(
      item => item.taskType === 'migrate-workflow-application'
    );
  }

  get latestWorkflowTask(): ScheduledTaskQueueItem | null {
    return this.workflowTasks[0] || null;
  }

  get latestCoordinatorTask(): ScheduledTaskQueueItem | null {
    return this.coordinatorTasks.reduce<ScheduledTaskQueueItem | null>(
      (latest, item) =>
        !latest || this.queuedTimestamp(item) > this.queuedTimestamp(latest)
          ? item
          : latest,
      null
    );
  }

  get activeCoordinatorTasks(): ScheduledTaskQueueItem[] {
    return this.coordinatorTasks
      .filter(item => {
        const status = this.getTaskStatus(item);
        return status === 'Queued' || status === 'Processing';
      })
      .sort((left, right) => this.queuedTimestamp(right) - this.queuedTimestamp(left));
  }

  get coordinatorStatus(): TaskStatus | 'Pending' {
    const activeTask = this.activeCoordinatorTasks[0];
    if (activeTask) {
      return this.getTaskStatus(activeTask);
    }
    if (this.latestCoordinatorTask) {
      return this.getTaskStatus(this.latestCoordinatorTask);
    }
    return this.processing?.applicationDataMigratedOn ? 'Completed' : 'Pending';
  }

  get workflowStatus(): TaskStatus {
    if (this.latestWorkflowTask) {
      return this.getTaskStatus(this.latestWorkflowTask);
    }

    if (this.processing?.workflowMigratedOn) {
      return 'Completed';
    }

    return 'Queued';
  }

  get appRows(): ApplicationTaskRow[] {
    return this.applicationTasks.map((item, index) => {
      const parameters = this.parseDataJson(item.dataJSON);
      const identifier =
        parameters?.applicationIdentifier ||
        this.getExternalIdFromParent(item.parentId) ||
        `Application ${index + 1}`;

      return {
        id: item.id,
        queueItem: item,
        applicationIdentifier: identifier,
        status: this.getTaskStatus(item),
        queuedOn: item.queuedOn,
        executedOn: item.executedOn,
        completedOn: item.completedOn,
        queuedDuration: this.formatQueuedDuration(item),
        executionDuration: this.formatExecutionDuration(item),
        errorMessage: item.errorMessage,
        workerLabel:
          item.executedOn && !item.completedOn
            ? 'Active worker'
            : 'Not assigned'
      };
    });
  }

  get completedApplications(): number {
    return this.appRows.filter(row => row.status === 'Completed').length;
  }

  get runningApplications(): number {
    return this.appRows.filter(row => row.status === 'Processing').length;
  }

  get queuedApplications(): number {
    return this.appRows.filter(row => row.status === 'Queued').length;
  }

  get failedApplications(): number {
    return this.appRows.filter(row => row.status === 'Error').length;
  }

  get cancelledApplications(): number {
    return this.appRows.filter(row => row.status === 'Cancelled').length;
  }

  get cancelledTasks(): number {
    return this.taskQueueItems.filter(
      item => this.getTaskStatus(item) === 'Cancelled'
    ).length;
  }

  get totalApplications(): number {
    return this.appRows.length;
  }

  get applicationProgressPercent(): number {
    if (this.totalApplications === 0) {
      return this.processing?.applicationDataMigratedOn ? 100 : 0;
    }

    return Math.round(
      (this.completedApplications / this.totalApplications) * 100
    );
  }

  get latestError(): string {
    const failed = this.appRows.find(row => row.status === 'Error');
    if (failed?.errorMessage) {
      return failed.errorMessage;
    }

    return this.latestWorkflowTask?.errorMessage?.startsWith('Cancelled by')
      ? ''
      : this.latestWorkflowTask?.errorMessage || '';
  }

  get workspaceCount(): number {
    return this.runningApplications;
  }

  setView(view: MonitorView): void {
    this.activeView = view;
  }

  refresh(): void {
    this.loadQueueItems();
  }

  canCancel(item: ScheduledTaskQueueItem | null | undefined): boolean {
    return (
      !!item &&
      this.getTaskStatus(item) === 'Queued' &&
      item.id !== this.cancellingTaskId
    );
  }

  cancelTask(item: ScheduledTaskQueueItem): void {
    if (!this.processing?.id || !this.canCancel(item) || !item.id) {
      return;
    }

    this.cancellingTaskId = item.id;
    this.dataService
      .cancelMigrationScheduledTaskQueueItem(this.processing.id, item.id)
      .subscribe({
        next: cancelled => {
          this.cancellingTaskId = '';
          if (!cancelled) {
            this.errorMessage =
              'The task could not be cancelled because it has already started.';
          }
          this.loadQueueItems(false);
        },
        error: err => {
          this.cancellingTaskId = '';
          this.errorMessage =
            err.error?.message ||
            err.message ||
            'Unable to cancel migration task.';
        }
      });
  }

  getTaskStatus(item: ScheduledTaskQueueItem): TaskStatus {
    if (item.errorMessage?.startsWith('Cancelled by')) {
      return 'Cancelled';
    }

    if (item.errorMessage) {
      return 'Error';
    }

    if (item.completedOn) {
      return 'Completed';
    }

    if (item.executedOn) {
      return 'Processing';
    }

    return 'Queued';
  }

  formatDateText(value?: Date): string {
    if (!value) {
      return '-';
    }

    return new Date(value).toLocaleString();
  }

  formatQueuedDuration(item?: ScheduledTaskQueueItem | null): string {
    if (!item?.queuedOn || !item.completedOn) {
      return '-';
    }

    return this.formatDuration(item.queuedOn, item.executedOn);
  }

  formatExecutionDuration(item?: ScheduledTaskQueueItem | null): string {
    if (!item?.executedOn || !item.completedOn) {
      return '-';
    }

    return this.formatDuration(item.executedOn, item.completedOn);
  }

  private startPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = timer(0, 10000).subscribe(() =>
      this.loadQueueItems(false)
    );
  }

  private loadQueueItems(showLoading = true): void {
    const migrationProcessingId = this.processing?.id;
    if (!migrationProcessingId) {
      return;
    }

    if (showLoading) {
      this.loading = true;
    }

    this.dataService
      .getMigrationScheduledTaskQueueItems(migrationProcessingId)
      .subscribe({
        next: items => {
          this.taskQueueItems = items || [];
          const completedWorkflowTask = this.workflowTasks.find(
            item => this.getTaskStatus(item) === 'Completed'
          );
          if (
            completedWorkflowTask?.id &&
            completedWorkflowTask.id !== this.notifiedWorkflowTaskId
          ) {
            this.notifiedWorkflowTaskId = completedWorkflowTask.id;
            this.workflowTaskCompleted.emit();
          }
          this.errorMessage = '';
          this.loading = false;
        },
        error: err => {
          this.errorMessage =
            err.error?.message ||
            err.message ||
            'Unable to load migration task status.';
          this.loading = false;
        }
      });
  }

  private parseDataJson(dataJson?: string): any {
    if (!dataJson) {
      return null;
    }

    try {
      return JSON.parse(dataJson);
    } catch {
      return null;
    }
  }

  private getExternalIdFromParent(parentId?: string): string {
    if (!parentId || parentId.indexOf(':') < 0) {
      return '';
    }

    return parentId.substring(parentId.indexOf(':') + 1);
  }

  private formatDuration(start?: Date, end?: Date): string {
    if (!start) {
      return '-';
    }

    const startTime = new Date(start).getTime();
    const endTime = end ? new Date(end).getTime() : Date.now();
    const seconds = Math.max(0, Math.round((endTime - startTime) / 1000));
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  private queuedTimestamp(item: ScheduledTaskQueueItem): number {
    return item.queuedOn ? new Date(item.queuedOn).getTime() : 0;
  }
}
